import { useTranslation } from 'react-i18next';
import { useEntityStore } from '../../stores/entityStore';
import { useIsMobile } from '../../hooks/useIsMobile';
import { EntityLink } from './EntityLink';
import { useCan } from '../../lib/rbac';
import {
  X, ArrowLeft, ChevronDown, ChevronUp, Loader2,
  Package, Truck, CreditCard, AlertTriangle, ShoppingCart,
  MapPin, FileText, Box
} from 'lucide-react';
import { useState } from 'react';

const TYPE_LABELS: Record<string, string> = {
  ml_order: 'Orden ML',
  ml_shipment: 'Envío ML',
  tracking: 'Tracking',
  po_number: 'Orden de Compra',
  asin: 'ASIN',
  amazon_order: 'Orden Amazon',
  wh_item: 'Item Warehouse',
};

const TYPE_COLORS: Record<string, string> = {
  ml_order: 'bg-orange-500/20 text-orange-400',
  ml_shipment: 'bg-blue-500/20 text-blue-400',
  tracking: 'bg-cyan-500/20 text-cyan-400',
  po_number: 'bg-purple-500/20 text-purple-400',
  asin: 'bg-teal-500/20 text-teal-400',
  amazon_order: 'bg-yellow-500/20 text-yellow-400',
  wh_item: 'bg-green-500/20 text-green-400',
};

function Section({ title, icon: Icon, count, children, defaultOpen = false }: {
  title: string; icon: any; count?: number; children: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (count === 0) return null;

  return (
    <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-[var(--color-bg)] hover:bg-[var(--color-bg-hover)] text-left"
      >
        <Icon size={14} className="text-[var(--color-text-secondary)] shrink-0" />
        <span className="text-sm font-medium text-[var(--color-text)] flex-1">{title}</span>
        {count !== undefined && (
          <span className="text-xs text-[var(--color-text-secondary)] bg-[var(--color-bg-secondary)] px-1.5 py-0.5 rounded">{count}</span>
        )}
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && <div className="p-3 space-y-2 border-t border-[var(--color-border)]">{children}</div>}
    </div>
  );
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-[var(--color-text-secondary)]">{label}</span>
      <span className="text-[var(--color-text)] text-right">{children}</span>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const colors: Record<string, string> = {
    paid: 'bg-green-500/20 text-green-400',
    cancelled: 'bg-red-500/20 text-red-400',
    ready_to_ship: 'bg-blue-500/20 text-blue-400',
    shipped: 'bg-cyan-500/20 text-cyan-400',
    delivered: 'bg-green-500/20 text-green-400',
    ordered: 'bg-yellow-500/20 text-yellow-400',
    opened: 'bg-red-500/20 text-red-400',
    closed: 'bg-gray-500/20 text-gray-400',
  };
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colors[status] || 'bg-gray-500/20 text-gray-400'}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
}

export function EntityDetailPanel() {
  const { t } = useTranslation();
  const { isOpen, entity, loading, error, current, history, close, goBack } = useEntityStore();
  const isMobile = useIsMobile();
  const can = useCan();
  // Todos los importes de este panel (total de la orden, precio por unidad, costo de compra,
  // costo de la OC, pago) son dinero: sin permiso van con guion o no se muestra la fila.
  const canMoney = can('global.view_money');

  if (!isOpen) return null;

  const e = entity;
  const sellerId = current?.sellerId || 1;

  return (
    <>
      {/* Backdrop */}
      {!isMobile && <div className="fixed inset-0 bg-black/30 z-[54]" onClick={close} />}

      {/* Panel */}
      <div className={`fixed z-[55] bg-[var(--color-bg-card)] border-l border-[var(--color-border)] shadow-2xl flex flex-col ${
        isMobile
          ? 'inset-0 border-l-0'
          : 'right-0 top-0 h-full w-[480px] max-w-[90vw]'
      }`}>
        {/* Header */}
        <div className="flex items-center gap-2 p-4 border-b border-[var(--color-border)] shrink-0">
          {history.length > 0 && (
            <button onClick={goBack} className="p-1 hover:bg-[var(--color-bg-hover)] rounded">
              <ArrowLeft size={16} className="text-[var(--color-text-secondary)]" />
            </button>
          )}
          {current && (
            <span className={`px-2 py-1 rounded text-xs font-medium ${TYPE_COLORS[current.type] || ''}`}>
              {TYPE_LABELS[current.type] || current.type}
            </span>
          )}
          <span className="font-mono text-sm text-[var(--color-text)] flex-1 truncate">
            {current?.value}
          </span>
          <button onClick={close} className="p-1 hover:bg-[var(--color-bg-hover)] rounded">
            <X size={16} className="text-[var(--color-text-secondary)]" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[var(--color-text-secondary)]" />
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>
          )}

          {!loading && e && !e.not_found && (
            <>
              {/* Order */}
              {e.order && (
                <Section title={t('entity.orderInfo')} icon={ShoppingCart} defaultOpen>
                  <KV label={t('wh.order')}>
                    <EntityLink type="ml_order" value={e.order.ml_order_id} sellerId={sellerId} />
                  </KV>
                  <KV label={t('common.status')}><StatusPill status={e.order.status} /></KV>
                  <KV label={t('wh.buyer')}>{e.order.buyer_nickname || '-'}</KV>
                  <KV label={t('common.date')}>{new Date(e.order.date_created).toLocaleString()}</KV>
                  {e.order.paid_amount && <KV label={t('common.total')}>{canMoney ? `$${e.order.paid_amount}` : '—'}</KV>}
                </Section>
              )}

              {/* Order Items */}
              {e.order_items?.length > 0 && (
                <Section title={t('entity.items')} icon={Package} count={e.order_items.length} defaultOpen>
                  {e.order_items.map((item: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-[var(--color-bg)] rounded">
                      {item.thumbnail && (
                        <img src={item.thumbnail} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-[var(--color-text)] truncate">{item.title}</p>
                        <div className="flex items-center gap-2 text-[10px] text-[var(--color-text-secondary)]">
                          {item.seller_sku && <EntityLink type="asin" value={item.seller_sku.substring(0, 10)} sellerId={sellerId} />}
                          <span>x{item.quantity}</span>
                          {item.unit_price && <span>{canMoney ? `$${item.unit_price}` : '—'}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </Section>
              )}

              {/* Shipments */}
              {e.shipments?.length > 0 && (
                <Section title={t('entity.shipments')} icon={Truck} count={e.shipments.length}>
                  {e.shipments.map((s: any, i: number) => (
                    <div key={i} className="space-y-1">
                      <KV label="Shipment">
                        <EntityLink type="ml_shipment" value={s.ml_shipment_id} sellerId={sellerId} />
                      </KV>
                      <KV label={t('common.status')}><StatusPill status={s.status} /></KV>
                      {s.tracking_number && (
                        <KV label="Tracking">
                          <EntityLink type="tracking" value={s.tracking_number} sellerId={sellerId} />
                        </KV>
                      )}
                    </div>
                  ))}
                </Section>
              )}

              {/* Amazon Purchases */}
              {e.amazon_purchases?.length > 0 && (
                <Section title={t('entity.amazonPurchases')} icon={ShoppingCart} count={e.amazon_purchases.length}>
                  {e.amazon_purchases.map((ap: any, i: number) => (
                    <div key={i} className="space-y-1 p-2 bg-[var(--color-bg)] rounded">
                      {ap.asin && <KV label="ASIN"><EntityLink type="asin" value={ap.asin} sellerId={sellerId} /></KV>}
                      <KV label={t('common.status')}><StatusPill status={ap.amazon_status} /></KV>
                      {ap.amazon_order_id && (
                        <KV label="Amazon Order">
                          <EntityLink type="amazon_order" value={ap.amazon_order_id} sellerId={sellerId} />
                        </KV>
                      )}
                      {ap.tracking_number && (
                        <KV label="Tracking">
                          <EntityLink type="tracking" value={ap.tracking_number} sellerId={sellerId} />
                        </KV>
                      )}
                      {ap.price && <KV label="Cost">{canMoney ? `$${ap.price}` : '—'}</KV>}
                    </div>
                  ))}
                </Section>
              )}

              {/* Stock POs */}
              {e.stock_pos?.length > 0 && (
                <Section title={t('entity.stockPOs')} icon={FileText} count={e.stock_pos.length}>
                  {e.stock_pos.map((po: any, i: number) => (
                    <div key={i} className="space-y-1">
                      {po.po_number && (
                        <KV label="PO">
                          <EntityLink type="po_number" value={po.po_number} sellerId={sellerId} />
                        </KV>
                      )}
                      {po.status && <KV label={t('common.status')}><StatusPill status={po.status} /></KV>}
                      {po.total_cost && <KV label="Cost">{canMoney ? `$${po.total_cost}` : '—'}</KV>}
                    </div>
                  ))}
                </Section>
              )}

              {/* Warehouse Items */}
              {e.warehouse_items?.length > 0 && (
                <Section title={t('entity.warehouseItems')} icon={Box} count={e.warehouse_items.length}>
                  {e.warehouse_items.map((wi: any, i: number) => (
                    <div key={i} className="space-y-1">
                      <KV label="WH Item">
                        <EntityLink type="wh_item" value={wi.id} sellerId={sellerId} />
                      </KV>
                      <KV label={t('common.status')}><StatusPill status={wi.status} /></KV>
                      {wi.asin && <KV label="ASIN"><EntityLink type="asin" value={wi.asin} sellerId={sellerId} /></KV>}
                      <KV label="Batch">#{wi.batch_id}</KV>
                    </div>
                  ))}
                </Section>
              )}

              {/* Claims */}
              {e.claims?.length > 0 && (
                <Section title={t('entity.claims')} icon={AlertTriangle} count={e.claims.length}>
                  {e.claims.map((cl: any, i: number) => (
                    <div key={i} className="space-y-1 p-2 bg-[var(--color-bg)] rounded">
                      <KV label="Claim">{cl.ml_claim_id}</KV>
                      <KV label={t('common.status')}><StatusPill status={cl.status} /></KV>
                      <KV label="Reason">{cl.reason_id || cl.type}</KV>
                    </div>
                  ))}
                </Section>
              )}

              {/* Tracking */}
              {e.tracking?.length > 0 && (
                <Section title={t('entity.tracking')} icon={MapPin} count={e.tracking.length}>
                  {e.tracking.map((tr: any, i: number) => (
                    <div key={i} className="space-y-1">
                      <KV label="Tracking">
                        <EntityLink type="tracking" value={tr.tracking_number} sellerId={sellerId} />
                      </KV>
                      {tr.events?.slice(0, 5).map((ev: any, j: number) => (
                        <div key={j} className="text-[10px] text-[var(--color-text-secondary)] pl-3 border-l border-[var(--color-border)]">
                          {new Date(ev.event_time).toLocaleString()} — {ev.description || ev.status}
                        </div>
                      ))}
                    </div>
                  ))}
                </Section>
              )}

              {/* Payments */}
              {e.payments?.length > 0 && (
                <Section title={t('entity.payments')} icon={CreditCard} count={e.payments.length}>
                  {e.payments.map((p: any, i: number) => (
                    <div key={i} className="space-y-1">
                      <KV label={t('common.status')}><StatusPill status={p.status} /></KV>
                      <KV label={t('common.total')}>{canMoney ? `${p.currency_id} $${p.total_paid_amount || p.transaction_amount}` : '—'}</KV>
                      <KV label="Method">{p.payment_type}</KV>
                    </div>
                  ))}
                </Section>
              )}

              {/* Products */}
              {e.products?.length > 0 && (
                <Section title={t('entity.products')} icon={Package} count={e.products.length}>
                  {e.products.map((p: any, i: number) => (
                    <div key={i} className="space-y-1">
                      <KV label="ASIN"><EntityLink type="asin" value={p.asin} sellerId={sellerId} /></KV>
                      <KV label="On Hand">{p.qty_on_hand ?? '-'}</KV>
                      <KV label="Available">{p.qty_available ?? '-'}</KV>
                      <KV label="Incoming">{p.qty_incoming ?? '-'}</KV>
                    </div>
                  ))}
                </Section>
              )}

              {/* Related Entities */}
              {e.related_entities?.length > 0 && (
                <Section title={t('entity.relatedEntities')} icon={Package} count={e.related_entities.length}>
                  {e.related_entities.map((re: any, i: number) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <EntityLink type={re.type || 'ml_order'} value={re.ml_order_id || re.value} sellerId={sellerId} />
                      {re.status && <StatusPill status={re.status} />}
                      {re.buyer_nickname && <span className="text-[var(--color-text-secondary)]">{re.buyer_nickname}</span>}
                    </div>
                  ))}
                </Section>
              )}
            </>
          )}

          {!loading && e?.not_found && (
            <div className="text-center py-12 text-[var(--color-text-secondary)]">
              <Package size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">{t('entity.notFound')}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
